import type { AppId, BuildingId, DecorationId, ShopItem } from "./types.ts";

export const MAX_LEVEL = 5;
/** Shown in the HUD next to the Keep shield. */
export const PLAYER_NAME = "Tausif";
export const TOWN_HALL_LEAD = 1;
export const LONDON_TZ = "Europe/London";

/**
 * Every levelling threshold lives here so the rules stay tunable.
 * Levels come only from real metrics — loot can never buy a level.
 */
export const THRESHOLDS = {
  /**
   * Town Hall (Life Planner): 90-day plan milestones hit.
   * 0 milestones still founds a L1 keep so the village exists;
   * further milestones raise it. Other buildings cannot exceed TH+1.
   */
  townHall: [0, 1, 3, 6, 10],

  /** Barracks (Search + Applications): applications, then interview, then offer. */
  barracks: {
    applications: [1, 10, 25],
    interviewLevel: 4,
    offerLevel: 5,
  },

  /** Gold Mine (Money): side-hustle GBP per month. */
  goldMine: [1, 100, 250, 500, 1000],

  /** Crystal Tanks: each app's MRR in USD. */
  crystalTank: [1, 50, 150, 300, 500],

  /** Lab (Upskill): lessons done. */
  lab: [1, 5, 15, 30, 50],

  /** Workshop (Builder): sequential shipping stages. */
  workshop: [
    "repoMade",
    "firstDeploy",
    "mvpLive",
    "firstUser",
    "firstPayingUser",
  ] as const,

  /** Watchtower (Socials / Baseerah): posts, then follower milestones. */
  watchtower: {
    posts: [1, 10, 30],
    followers: [1_000, 10_000],
  },

  /** Builder huts (Motivator): lifetime daily actions completed. */
  builderHut: [1, 7, 21, 60, 100],

  /** Walls (Debug): HP bands used only for the level badge, not upgrades. */
  wallsHpBands: [1, 200, 400, 700, 950],
} as const;

export const WALL = {
  maxHp: 950,
  startingHp: 620,
  hpPerBugFixed: 20,
  raidDamageMin: 80,
  raidDamageMax: 180,
  raidStealRatio: 0.2,
} as const;

export const LOOT = {
  startingGold: 12_000,
  startingCrystals: 1_100,
  maxCarry: 99_999,
  baseCollectGold: 20,
  baseCollectCrystals: 10,
  goldPerMineLevel: 40,
  crystalsPerTankLevel: 15,
  sprintGold: 300,
  sprintCrystals: 40,
} as const;

export const SPRINT = {
  durationMs: 25 * 60 * 1000,
  fastDurationMs: 5_000,
} as const;

export const SHOP_ITEMS: ShopItem[] = [
  {
    id: "repair-walls",
    kind: "repair",
    name: "Patch the walls",
    description: "Stone masons patch raid damage.",
    goldCost: 400,
    crystalCost: 0,
    wallHp: 100,
  },
  {
    id: "rebuild-walls",
    kind: "repair",
    name: "Full rebuild",
    description: "Rebuild every wall to full strength.",
    goldCost: 0,
    crystalCost: 120,
    restoreFull: true,
  },
  {
    id: "crescentLantern",
    kind: "decoration",
    name: "Crescent lantern",
    description: "A warm lantern with a crescent moon on top.",
    goldCost: 300,
    crystalCost: 0,
    decorationId: "crescentLantern",
  },
  {
    id: "flowerBed",
    kind: "decoration",
    name: "Flower bed",
    description: "A ring of village flowers.",
    goldCost: 150,
    crystalCost: 0,
    decorationId: "flowerBed",
  },
  {
    id: "fountain",
    kind: "decoration",
    name: "Fountain",
    description: "A small stone fountain for the square.",
    goldCost: 0,
    crystalCost: 60,
    decorationId: "fountain",
  },
  {
    id: "datePalm",
    kind: "decoration",
    name: "Date palm",
    description: "A tall palm heavy with dates.",
    goldCost: 400,
    crystalCost: 0,
    decorationId: "datePalm",
  },
  {
    id: "villageBanner",
    kind: "decoration",
    name: "Village banner",
    description: "A tall banner in the village colours.",
    goldCost: 200,
    crystalCost: 0,
    decorationId: "villageBanner",
  },
  {
    id: "gardenBench",
    kind: "decoration",
    name: "Garden bench",
    description: "A wooden bench for slow evenings.",
    goldCost: 250,
    crystalCost: 0,
    decorationId: "gardenBench",
  },
];

export type BuildingMeta = {
  id: BuildingId;
  name: string;
  bot: string;
  metric: string;
  valueKind: "count" | "gbp" | "usd" | "hp" | "stage" | "mixed";
};

export const BUILDING_META: Record<BuildingId, BuildingMeta> = {
  townHall: {
    id: "townHall",
    name: "Keep",
    bot: "Life Planner",
    metric: "90-day plan milestones hit",
    valueKind: "count",
  },
  barracks: {
    id: "barracks",
    name: "Barracks",
    bot: "Search + Applications",
    metric: "Applications sent (then first interview / offer)",
    valueKind: "mixed",
  },
  goldMine: {
    id: "goldMine",
    name: "Gold Mine",
    bot: "Money",
    metric: "Side-hustle GBP per month",
    valueKind: "gbp",
  },
  crystalTankRevenue: {
    id: "crystalTankRevenue",
    name: "Crystal Tank — Revenue",
    bot: "Revenue",
    metric: "Revenue app MRR (USD)",
    valueKind: "usd",
  },
  crystalTankViralz: {
    id: "crystalTankViralz",
    name: "Crystal Tank — Viralz",
    bot: "Viralz",
    metric: "Viralz MRR (USD)",
    valueKind: "usd",
  },
  crystalTankTikwave: {
    id: "crystalTankTikwave",
    name: "Crystal Tank — Tikwave",
    bot: "Tikwave",
    metric: "Tikwave MRR (USD)",
    valueKind: "usd",
  },
  crystalTankGoGrow: {
    id: "crystalTankGoGrow",
    name: "Crystal Tank — GoGrow",
    bot: "GoGrow",
    metric: "GoGrow MRR (USD)",
    valueKind: "usd",
  },
  crystalTankRihla: {
    id: "crystalTankRihla",
    name: "Crystal Tank — Rihla",
    bot: "Rihla",
    metric: "Rihla MRR (USD)",
    valueKind: "usd",
  },
  lab: {
    id: "lab",
    name: "Lab",
    bot: "Upskill",
    metric: "Lessons done",
    valueKind: "count",
  },
  workshop: {
    id: "workshop",
    name: "Workshop",
    bot: "Builder",
    metric: "Shipping stage (repo → deploy → MVP → user → paying user)",
    valueKind: "stage",
  },
  watchtower: {
    id: "watchtower",
    name: "Watchtower",
    bot: "Socials (Baseerah)",
    metric: "Posts, then followers",
    valueKind: "mixed",
  },
  walls: {
    id: "walls",
    name: "Walls",
    bot: "Debug",
    metric: "Wall HP (bugs fixed repair them)",
    valueKind: "hp",
  },
  builderHut: {
    id: "builderHut",
    name: "Builder Hut",
    bot: "Motivator",
    metric: "Daily actions completed",
    valueKind: "count",
  },
};

export const APP_BUILDINGS: Record<AppId, BuildingId> = {
  revenue: "crystalTankRevenue",
  viralz: "crystalTankViralz",
  tikwave: "crystalTankTikwave",
  gogrow: "crystalTankGoGrow",
  rihla: "crystalTankRihla",
};

export const DECORATION_META: Record<DecorationId, { name: string }> = {
  crescentLantern: { name: "Crescent lantern" },
  flowerBed: { name: "Flower bed" },
  fountain: { name: "Fountain" },
  datePalm: { name: "Date palm" },
  villageBanner: { name: "Village banner" },
  gardenBench: { name: "Garden bench" },
};

export function shopItemById(id: string): ShopItem | undefined {
  return SHOP_ITEMS.find((item) => item.id === id);
}
